import { LanguageToggle, useT } from '@/lib/i18n';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, MessageCircle } from 'lucide-react';
import { Brand, PRODUCT_NAME } from '@/components/shared/brand';

const POINTS = [
  'WhatsApp, Instagram, Telegram and your website land in one inbox.',
  'Deals move on boards; quotes and invoices go out with 5% VAT.',
  'Built for the UAE: AED, Arabic, Emirates and free zones.',
] as const;

/** The little pipeline on the sign-in screen: what the product does, shown. */
const STAGES = [
  { name: 'New Enquiry', colour: '#64748b', deals: [['Staff uniforms 2027', 'AED 18,000'], ['Annual cleaning', 'AED 125,000']] },
  { name: 'Qualified', colour: '#0ea5e9', deals: [['Cloud migration', 'AED 210,000']] },
  { name: 'Proposal Sent', colour: '#8b5cf6', deals: [['Clinic onboarding', 'AED 36,500']] },
] as const;

/**
 * The shared frame for every signed-out screen: the form on a clean canvas,
 * and beside it (from `lg`) a warm panel with a glimpse of the product.
 */
export function AuthLayout({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const t = useT();
  return (
    <div className="flex min-h-screen bg-card">
      <main className="relative flex flex-1 flex-col px-6 py-6 sm:px-10">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" aria-label={PRODUCT_NAME}>
            <Brand size="sm" />
          </Link>
          <div className="flex items-center gap-1">
            <Link
              to="/"
              className="hidden items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:flex"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('Back to site')}
            </Link>
            <LanguageToggle />
          </div>
        </div>

        <div className="mx-auto my-auto w-full max-w-[25rem] py-10">
          <h1 className="text-[1.85rem] font-bold leading-tight text-foreground">{t(title)}</h1>
          {description && <p className="mt-2 text-[0.95rem] text-muted-foreground">{t(description)}</p>}

          <div className="mt-7">{children}</div>

          {footer && <div className="mt-6 text-sm text-muted-foreground">{footer}</div>}
        </div>

        <p className="text-xs text-muted-foreground/80">
          © {new Date().getFullYear()} {PRODUCT_NAME} · Dubai, United Arab Emirates
        </p>
      </main>

      <aside className="relative hidden w-[48%] max-w-[46rem] shrink-0 overflow-hidden bg-[#fff3ee] lg:flex lg:flex-col lg:justify-center dark:bg-[hsl(12_30%_10%)]">
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden />
        <div className="relative px-14 py-12">
          <h2 className="max-w-md text-[2.1rem] font-bold leading-[1.12] text-slate-900 dark:text-white">
            {t('Every conversation.')} <span className="text-primary">{t('Every deal.')}</span> {t('One place.')}
          </h2>
          <ul className="mt-6 max-w-md space-y-2.5">
            {POINTS.map((point) => (
              <li key={point} className="flex items-start gap-2.5 text-[0.95rem] text-slate-700 dark:text-white/75">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                {t(point)}
              </li>
            ))}
          </ul>

          {/* A miniature deals board. */}
          <div className="mt-10 rounded-xl border border-slate-200 bg-white p-3 shadow-brand-lg dark:border-white/10 dark:bg-slate-900" dir="ltr">
            <div className="mb-3 flex items-center gap-2 px-1">
              <span className="text-sm font-bold text-slate-900 dark:text-white">Deals</span>
              <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-bold text-slate-500 dark:bg-white/10 dark:text-white/60">4</span>
              <span className="ms-auto rounded-md bg-primary px-2 py-1 text-[10px] font-bold text-white">+ Add deal</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {STAGES.map((stage) => (
                <div key={stage.name} className="rounded-lg bg-slate-100 p-2 dark:bg-white/5">
                  <span className="rounded px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ backgroundColor: stage.colour }}>
                    {stage.name}
                  </span>
                  <div className="mt-2 space-y-1.5">
                    {stage.deals.map(([name, value]) => (
                      <div key={name} className="rounded-md border border-slate-200 bg-white p-2 dark:border-white/10 dark:bg-slate-800">
                        <p className="truncate text-[11px] font-semibold text-sky-800 dark:text-sky-300">{name}</p>
                        <p className="text-[10px] text-slate-500 dark:text-white/50">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative -mt-6 ms-auto w-64 rounded-xl border border-slate-200 bg-white p-3 shadow-brand-lg dark:border-white/10 dark:bg-slate-900" dir="ltr">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#25D366] text-white">
                <MessageCircle className="h-3.5 w-3.5" />
              </span>
              <span className="text-[12px] font-semibold text-slate-900 dark:text-white">Aisha K. · WhatsApp</span>
            </div>
            <p className="mt-2 rounded-lg rounded-tl-sm bg-slate-100 px-2.5 py-1.5 text-[11px] text-slate-700 dark:bg-white/10 dark:text-white/80">
              Is the trade licence package still AED 12,500?
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}

/** Consistent inline error above a form's submit button. */
export function FormError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {message}
    </p>
  );
}
